import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Calculator, TrendingUp, History, Settings, Copy, Check,
  ChevronDown, ChevronUp, Trash2, Plus, Edit3, X, Search,
  Printer, AlertTriangle, CheckCircle, Target, Package,
  ChevronRight, Save, RefreshCw, Sliders, ExternalLink,
  Download, Star, Tag, Smartphone, FileText, List,
  ShoppingCart, BookOpen, Filter, Zap, Percent
} from 'lucide-react';
import { toast } from 'sonner';
import { CatalogModal }       from './pricing/CatalogModal';
import { SupplierPanel }      from './pricing/SupplierPanel';
import { PrintConfigModal }   from './pricing/PrintConfigModal';
import { LabelPreviewModal }  from './pricing/LabelPreviewModal';

const PRINT_CFG_KEY = 'phonelab_print_config';

const API_URL = window.location.origin;
const authHdr = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('admin_token')}`
});

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt  = (n) => (n == null || isNaN(n)) ? '—' : n.toFixed(2).replace('.', ',') + ' €';
const fmtP = (n) => (n == null || isNaN(n)) ? '—' : n.toFixed(1) + '%';
const margeColor = (tm) => tm >= 55 ? 'text-green-400' : tm >= 35 ? 'text-yellow-400' : 'text-red-400';

// ── Constants ─────────────────────────────────────────────────────────────────
// Fallback hardcodé — remplacé par qualityTypes dynamiques du serveur
const QUALITY_MAP = {
  origine:            { label: 'Origine / OEM',        icon: '🏆', warranty: '12-24 mois', color: 'text-yellow-400' },
  premium_oled:       { label: 'Premium OLED',          icon: '💎', warranty: '6 mois',     color: 'text-blue-400' },
  premium_plus_fhd:   { label: 'Premium Plus FHD',      icon: '✨', warranty: '6 mois',     color: 'text-purple-400' },
  premium:            { label: 'Premium',               icon: '⭐', warranty: '6 mois',     color: 'text-yellow-300' },
  original_refurb:    { label: 'Original Refurb',       icon: '♻️', warranty: '3-6 mois',   color: 'text-green-400' },
  entree_gamme:       { label: 'Entrée de gamme',       icon: '💧', warranty: '3 mois',     color: 'text-gray-400' },
};
const COMPLEXITY = {
  simple:   { label: 'Simple',   desc: 'Écran, batterie',    coef: '×1.0'  },
  moyen:    { label: 'Moyen',    desc: 'Connecteur, bouton', coef: '×1.15' },
  complexe: { label: 'Complexe', desc: 'Micro-soudure',      coef: '×1.35' },
};
const TYPE_LABELS = { brand: 'Marque', model: 'Modèle', part_type: 'Catégorie' };

// ── Live price computation (pure function) ─────────────────────────────────
function computeLive(achat, quality, complexity, concurrence, config, customPrice) {
  const pa = parseFloat(achat);
  if (!pa || pa <= 0 || !config) return null;
  const qc  = config.quality_coefficients?.[quality] ?? 2.3;
  const cc  = config.complexity_multipliers?.[complexity] ?? 1.0;
  const tva = config.tva_rate ?? 0.20;
  let pvHT = pa * qc * cc;
  let pvTTC = pvHT * (1 + tva);
  const suggested_ttc = pvTTC;
  if (customPrice && parseFloat(customPrice) > 0) {
    pvTTC = parseFloat(customPrice);
    pvHT  = pvTTC / (1 + tva);
  }
  const mb   = pvHT - pa;
  const tm   = pvHT > 0 ? (mb / pvHT) * 100 : 0;
  const conc = parseFloat(concurrence) || null;
  const ecart = conc ? pvTTC - conc : null;
  return { pvHT, pvTTC, mb, tm, ecart, conc, suggested_ttc, tva, qc, cc };
}

// ── Génère le HTML du ticket ───────────────────────────────────────────────────
function buildTicketHTML({ brand, model, part, quality, complexity, result, notes, clientName, repairNum, pickupDate, width = 80, fontSize = 10, margin = 3, lineHeight = 1.4, boldHeader = true }) {
  const now = new Date().toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const q = QUALITY_MAP[quality] || { label: quality, warranty: '', icon: '' };
  const c = COMPLEXITY[complexity] || { label: complexity };
  const deviceLabel = [brand, model, part].filter(Boolean).join(' · ') || 'Réparation';

  // Format pickup date
  let pickupFormatted = '';
  if (pickupDate) {
    try {
      const d = new Date(pickupDate);
      pickupFormatted = d.toLocaleString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { pickupFormatted = pickupDate; }
  }

  const clientSection = (clientName || repairNum || pickupFormatted) ? `
    <div class="sep"></div>
    ${clientName  ? `<div class="row"><span>Client</span><span><strong>${clientName}</strong></span></div>` : ''}
    ${repairNum   ? `<div class="row"><span>N° Réparation</span><span><strong>${repairNum}</strong></span></div>` : ''}
    ${pickupFormatted ? `<div class="row"><span>Récupération</span><span>${pickupFormatted}</span></div>` : ''}` : '';

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=${width}mm">
  <title>Ticket PhoneLab 69</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    @media print {
      @page {
        size: ${width}mm auto;
        margin: 0mm;
      }
      html, body { width: ${width}mm; }
    }
    html, body {
      font-family: 'Courier New', Courier, monospace;
      font-size: ${fontSize}pt;
      line-height: ${lineHeight};
      width: ${width}mm;
      max-width: ${width}mm;
      background: #fff;
      color: #000;
    }
    .wrap { padding: ${margin}mm; }
    h1  { text-align: center; font-size: ${fontSize + 2}pt; margin: 2mm 0 1mm; letter-spacing: 1px; font-weight: ${boldHeader ? 'bold' : 'normal'}; }
    .sub { text-align: center; font-size: ${fontSize - 1}pt; color: #444; margin-bottom: 1mm; }
    .sep { border-top: 1px dashed #000; margin: 2mm 0; }
    .row { display: flex; justify-content: space-between; align-items: baseline; margin: 0.8mm 0; font-size: ${fontSize}pt; }
    .big { font-size: ${fontSize + 8}pt; font-weight: bold; text-align: center; margin: 3mm 0; letter-spacing: 1px; }
    .device { font-size: ${fontSize + 1}pt; font-weight: bold; margin: 1mm 0; }
    .small { font-size: ${fontSize - 1}pt; color: #555; margin: 0.5mm 0; }
    .footer { text-align: center; font-size: ${fontSize - 1}pt; color: #555; }
  </style>
</head>
<body>
<div class="wrap">
  <h1>★ PHONELAB 69 ★</h1>
  <div class="sub">Réparation Express • Villeurbanne</div>
  <div class="sep"></div>
  <div class="sub">${now}</div>
  <div class="sep"></div>
  <div class="device">${deviceLabel}</div>
  <div class="small">${q.icon ? q.icon + ' ' : ''}${q.label} • ${c.label}${q.warranty ? ' • Garantie : ' + q.warranty : ''}</div>
  ${notes ? `<div class="small">Note : ${notes}</div>` : ''}
  ${clientSection}
  <div class="sep"></div>
  <div class="row"><span>Prix HT</span><span>${fmt(result.pvHT)}</span></div>
  <div class="row"><span>TVA (${Math.round(result.tva * 100)}%)</span><span>${fmt(result.pvTTC - result.pvHT)}</span></div>
  <div class="sep"></div>
  <div class="big">${fmt(result.pvTTC)}&nbsp;TTC</div>
  <div class="sep"></div>
  <div class="footer">Merci de votre confiance !</div>
  <div class="footer">04 26 00 82 10</div>
</div>
</body>
</html>`;
}

const LABEL_CFG_KEY = 'phonelab_label_config';

// ── Génère le HTML d'une étiquette (Clabel CT221b) ──────────────────────────
function buildLabelHTML({ brand, model, part, quality, result, clientName, clientPhone, repairNum, notes, pinCode, labelWidth = 50, labelHeight = 30, fontSizes, showFields }) {
  const today = new Date();
  const dateStr = `${String(today.getDate()).padStart(2,'0')}/${String(today.getMonth()+1).padStart(2,'0')}/${String(today.getFullYear()).slice(-2)}`;

  // Chargement config étiquette (surcharge éventuelle)
  let cfg = { fontSizes: {}, showFields: {} };
  try { cfg = JSON.parse(localStorage.getItem('phonelab_label_config')) || cfg; } catch {}
  const fs = fontSizes || cfg.fontSizes || {};
  const sh = showFields || cfg.showFields || {};

  const get = (key, def) => (fs[key] != null ? fs[key] : def);
  const show = (key) => (sh[key] != null ? sh[key] : true);

  const productLine = [brand, model, notes || part].filter(Boolean).join(' · ');

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="utf-8">
  <title>Étiquette PhoneLab 69</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    @media print {
      @page { size: ${labelWidth}mm ${labelHeight}mm; margin: 0; }
      html, body { width: ${labelWidth}mm; height: ${labelHeight}mm; overflow: hidden; }
    }
    html, body {
      font-family: 'Arial Narrow', Arial, sans-serif;
      width: ${labelWidth}mm; height: ${labelHeight}mm;
      background: #fff; color: #000; overflow: hidden;
    }
    .wrap { padding: 1mm 1.5mm; display: flex; flex-direction: column; height: 100%; gap: 0; }
    .row  { display: flex; justify-content: space-between; align-items: flex-start; }
    .pin  { border: 0.5pt solid #000; padding: 0 1mm; border-radius: 0.5mm; }
    .trunc{ white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  </style>
</head>
<body><div class="wrap">
  ${(show('numTicket') || show('date')) ? `
  <div class="row">
    ${show('numTicket') && repairNum ? `<span style="font-size:${get('numTicket',20)}px;font-weight:bold;">#${repairNum}</span>` : '<span></span>'}
    ${show('date') ? `<span style="font-size:${get('date',12)}px;color:#555;">${dateStr}</span>` : ''}
  </div>` : ''}
  ${show('nomClient') && clientName ? `<div class="trunc" style="font-size:${get('nomClient',16)}px;font-weight:bold;">${clientName}</div>` : ''}
  ${show('telephone') && clientPhone ? `<div style="font-size:${get('telephone',28)}px;font-weight:bold;letter-spacing:0.5px;">${clientPhone}</div>` : ''}
  ${show('panne') && productLine ? `<div class="trunc" style="font-size:${get('panne',16)}px;color:#333;">${productLine}</div>` : ''}
  <div class="row" style="margin-top:auto;">
    ${show('prix') && result?.pvTTC ? `<span style="font-size:${get('prix',24)}px;font-weight:bold;">${fmt(result.pvTTC)}€</span>` : '<span></span>'}
    ${show('pinCode') && pinCode ? `<span class="pin" style="font-size:${get('pinCode',14)}px;font-weight:bold;">${pinCode}</span>` : ''}
  </div>
</div></body></html>`;
}

// ── Génère le HTML d'une étiquette VENTE produit (photo style) ───────────────
function buildSaleLabelHTML({ brand, model, storage, saleCondition, batteryPct, paymentText, result, labelWidth = 50, labelHeight = 40 }) {
  const productName = [brand, model].filter(Boolean).join(' ');
  const price       = result?.pvTTC ? `${fmt(result.pvTTC)}` : '—';

  // Calcul tailles adaptatives selon largeur
  const scale  = labelWidth / 50;
  const fName  = Math.round(15 * scale);
  const fPrice = Math.round(22 * scale);
  const fSmall = Math.round(8.5 * scale);
  const fPay   = Math.round(11 * scale);

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="utf-8">
  <title>Étiquette vente PhoneLab 69</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    @media print {
      @page { size: ${labelWidth}mm ${labelHeight}mm; margin: 0; }
      html, body { width: ${labelWidth}mm; height: ${labelHeight}mm; overflow: hidden; }
    }
    html, body {
      font-family: Arial, 'Arial Rounded MT Bold', sans-serif;
      width: ${labelWidth}mm; height: ${labelHeight}mm;
      background: #fff; color: #000; overflow: hidden;
    }
    .wrap    { padding: 1.5mm 2mm; display: flex; flex-direction: column; height: 100%; gap: 0.8mm; }
    .name    { font-size: ${fName}pt; font-weight: 900; line-height: 1.1; }
    .row2    { display: flex; justify-content: space-between; align-items: flex-end; }
    .storage { font-size: ${fSmall}pt; font-weight: 400; }
    .price   { font-size: ${fPrice}pt; font-weight: 900; line-height: 1; }
    .info    { font-size: ${fSmall}pt; font-weight: 400; line-height: 1.3; }
    .payment { font-size: ${fPay}pt; font-weight: 900; margin-top: auto; }
  </style>
</head>
<body><div class="wrap">
  <div class="name">${productName || 'Produit'}</div>
  <div class="row2">
    <div>${storage ? `<div class="storage">${storage}</div>` : ''}
    ${saleCondition ? `<div class="info">État: ${saleCondition}</div>` : ''}</div>
    <div class="price">${price}€</div>
  </div>
  ${batteryPct ? `<div class="info">Batterie : ${batteryPct}%</div>` : ''}
  ${paymentText ? `<div class="payment">${paymentText}</div>` : ''}
</div></body></html>`;
}

// ── Impression étiquette VENTE — QZ Tray ou fallback navigateur ───────────────
async function printSaleLabel(params) {
  let cfg = null;
  try { cfg = JSON.parse(localStorage.getItem(LABEL_CFG_KEY)); } catch {}

  const html = buildSaleLabelHTML({
    ...params,
    labelWidth:  cfg?.labelWidth  ?? 50,
    labelHeight: cfg?.labelHeight ?? 40,
  });

  if (cfg?.useLabelPrinter && cfg?.labelPrinter && typeof window.qz !== 'undefined') {
    try {
      if (!window.qz.websocket.isActive()) {
        await window.qz.websocket.connect({ retries: 3, delay: 1 });
      }
      const qzCfg = window.qz.configs.create(cfg.labelPrinter, { colorType: 'blackwhite', copies: 1 });
      await window.qz.print(qzCfg, [{ type: 'html', format: 'plain', data: html }]);
      toast.success('Étiquette vente imprimée via QZ Tray !');
      return;
    } catch (e) {
      toast.error(`QZ Tray: ${e.message} — bascule sur navigateur...`);
    }
  }
  const w = window.open('', '_blank', 'width=280,height=220,toolbar=0,menubar=0,scrollbars=0');
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => { w.focus(); w.print(); }, 400); }
}


// ── Impression étiquette réparation — QZ Tray (Clabel CT221b) ou fallback ──────
async function printLabel(params) {
  let cfg = null;
  try { cfg = JSON.parse(localStorage.getItem(LABEL_CFG_KEY)); } catch {}

  const html = buildLabelHTML({
    ...params,
    labelWidth:  cfg?.labelWidth  ?? 50,
    labelHeight: cfg?.labelHeight ?? 30,
    fontSizes:   cfg?.fontSizes,
    showFields:  cfg?.showFields,
  });

  if (cfg?.useLabelPrinter && cfg?.labelPrinter && typeof window.qz !== 'undefined') {
    try {
      if (!window.qz.websocket.isActive()) {
        await window.qz.websocket.connect({ retries: 3, delay: 1 });
      }
      const qzCfg = window.qz.configs.create(cfg.labelPrinter, {
        colorType: 'blackwhite',
        copies:    1,
      });
      await window.qz.print(qzCfg, [{ type: 'html', format: 'plain', data: html }]);
      toast.success('Étiquette imprimée via QZ Tray !');
      return;
    } catch (e) {
      toast.error(`QZ Tray: ${e.message} — bascule sur navigateur...`);
    }
  }

  // Fallback navigateur
  const w = window.open('', '_blank', 'width=250,height=200,toolbar=0,menubar=0,scrollbars=0');
  if (w) {
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 400);
  }
}

// ── POS Ticket — QZ Tray ou fallback navigateur ────────────────────────────────
async function printPOS(params) {
  let cfg = null;
  try { cfg = JSON.parse(localStorage.getItem(PRINT_CFG_KEY)); } catch {}

  const html = buildTicketHTML({
    ...params,
    width:      cfg?.width      ?? 80,
    fontSize:   cfg?.fontSize   ?? 10,
    margin:     cfg?.margin     ?? 3,
    lineHeight: cfg?.lineHeight ?? 1.4,
    boldHeader: cfg?.boldHeader ?? true,
  });

  // QZ Tray — on ne passe PAS size dans la config HTML :
  // QZ Tray crée un Chromium virtuel qui respecte le @page CSS du HTML.
  // Passer size ici forcerait le driver Windows à utiliser une taille fixe (ex: label 58x30).
  if (cfg?.useQZ && cfg?.printer && typeof window.qz !== 'undefined') {
    try {
      if (!window.qz.websocket.isActive()) {
        await window.qz.websocket.connect({ retries: 3, delay: 1 });
      }
      const qzCfg = window.qz.configs.create(cfg.printer, {
        colorType: 'blackwhite',
        copies:    1,
      });
      await window.qz.print(qzCfg, [{ type: 'html', format: 'plain', data: html }]);
      toast.success('Ticket imprimé via QZ Tray !');
      return;
    } catch (e) {
      toast.error(`QZ Tray: ${e.message} — bascule sur navigateur...`);
    }
  }

  // Fallback: fenêtre navigateur (l'utilisateur sélectionne lui-même le bon papier)
  const w = window.open('', '_blank', 'width=960,height=700,toolbar=0,menubar=0,scrollbars=1');
  if (w) {
    w.document.write(html);
    w.document.close();
    // Délai pour que le contenu se charge avant d'ouvrir l'impression
    setTimeout(() => { w.focus(); w.print(); }, 400);
  }
}

// ── Inline Add Input ──────────────────────────────────────────────────────────
const InlineAdd = ({ type, brandId, onAdded, onCancel }) => {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const save = async () => {
    if (!name.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      const body = { name: name.trim(), type };
      if (type === 'model' && brandId) body.parent_id = brandId;
      const r = await fetch(`${API_URL}/api/pricing/categories`, { method: 'POST', headers: authHdr(), body: JSON.stringify(body) });
      if (!r.ok) throw new Error((await r.json()).detail);
      const cat = await r.json();
      toast.success(`"${cat.name}" ajouté !`);
      onAdded(cat);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex items-center gap-1 mt-1">
      <input ref={inputRef} value={name} onChange={e => setName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') onCancel(); }}
        placeholder={`Nouveau ${TYPE_LABELS[type].toLowerCase()}...`}
        className="flex-1 bg-gray-800 border border-gray-600 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-500 placeholder-gray-600 min-w-0" />
      <button onClick={save} disabled={saving} className="p-1.5 bg-amber-500 hover:bg-amber-400 text-white rounded-lg transition-all">
        <Check size={11} />
      </button>
      <button onClick={onCancel} className="p-1.5 bg-gray-700 hover:bg-gray-600 text-gray-400 rounded-lg transition-all border border-gray-600">
        <X size={11} />
      </button>
    </div>
  );
};

// ── LEFT PANEL — arborescence Marque → Modèle → Catégorie ────────────────────
const LeftPanel = ({ categories, onCatsRefresh, onOpenCatalog }) => {
  const [expandedBrands, setExpandedBrands] = useState({});
  const [expandedModels, setExpandedModels] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const brands    = categories.filter(c => c.type === 'brand');
  const allModels = categories.filter(c => c.type === 'model');
  const partTypes = categories.filter(c => c.type === 'part_type');

  // Détecte l'onglet catalogue correspondant au nom d'une catégorie
  const getTabFromPartName = (name) => {
    const lower = (name || '').toLowerCase();
    if (['écran', 'ecran', 'screen', 'display', 'oled', 'lcd'].some(k => lower.includes(k))) return 'ecran';
    if (['batterie', 'battery', 'accu'].some(k => lower.includes(k))) return 'batterie';
    if (['connecteur', 'connector', 'charge', 'lightning', 'usb'].some(k => lower.includes(k))) return 'connecteur';
    if (['arrière', 'arriere', 'dos', 'vitre arrière', 'back', 'coque'].some(k => lower.includes(k))) return 'arriere';
    return 'all';
  };

  const toggleBrand = (id) => setExpandedBrands(p => ({ ...p, [id]: !p[id] }));
  const toggleModel = (id) => setExpandedModels(p => ({ ...p, [id]: !p[id] }));

  const saveEdit = async (id) => {
    if (!editName.trim()) { setEditingId(null); return; }
    try { await fetch(`${API_URL}/api/pricing/categories/${id}`, { method: 'PUT', headers: authHdr(), body: JSON.stringify({ name: editName }) }); toast.success('Renommé'); setEditingId(null); onCatsRefresh(); } catch { toast.error('Erreur'); }
  };

  const deleteCat = async (id, name) => {
    if (!window.confirm(`Supprimer "${name}" ?`)) return;
    await fetch(`${API_URL}/api/pricing/categories/${id}`, { method: 'DELETE', headers: authHdr() });
    toast.success('Supprimé'); onCatsRefresh();
  };

  // Editable category label
  const EditableName = ({ cat, indent = 0 }) => (
    editingId === cat.id
      ? <input autoFocus value={editName} onChange={e => setEditName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') saveEdit(cat.id); if (e.key === 'Escape') setEditingId(null); }}
          className="flex-1 bg-gray-700 text-white text-sm rounded px-2 py-0.5 focus:outline-none min-w-0 border border-gray-600"
          onClick={e => e.stopPropagation()} />
      : <span className="flex-1 text-sm truncate">{cat.name}</span>
  );

  const CatActions = ({ cat }) => (
    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
      <button onClick={e => { e.stopPropagation(); setEditingId(cat.id); setEditName(cat.name); }} className="p-1 text-gray-400 hover:text-blue-500"><Edit3 size={10} /></button>
      <button onClick={e => { e.stopPropagation(); deleteCat(cat.id, cat.name); }} className="p-1 text-gray-400 hover:text-red-500"><Trash2 size={10} /></button>
    </div>
  );

  return (
    <div className="w-72 shrink-0 bg-gray-900 border border-gray-700 rounded-2xl flex flex-col overflow-hidden shadow-lg" style={{ maxHeight: 'calc(100vh - 190px)' }}>

      {/* Header arborescence */}
      <div className="px-3 py-3 border-b border-gray-700 shrink-0 bg-gray-800">
        <p className="text-xs font-bold uppercase tracking-widest text-gray-300">📱 Catalogue</p>
        <p className="text-[10px] text-gray-500 mt-0.5">Cliquez une catégorie → ouvre le catalogue</p>
      </div>

      <div className="overflow-y-auto flex-1 text-gray-300 pt-1 pb-2">

        {/* ── Arborescence Marques → Modèles → Catégories ── */}
        {brands.map(brand => {
          const brandModels = allModels.filter(m => m.parent_id === brand.id);
          const isExpBrand = !!expandedBrands[brand.id];
          return (
            <div key={brand.id}>
              {/* Ligne Marque */}
              <div className="group flex items-center gap-2 px-3 py-2 mx-1 rounded-xl hover:bg-gray-700 cursor-pointer transition-all">
                <button onClick={() => toggleBrand(brand.id)} className="flex-1 flex items-center gap-2 text-left min-w-0">
                  {isExpBrand
                    ? <ChevronDown size={12} className="text-gray-500 shrink-0" />
                    : <ChevronRight size={12} className="text-gray-500 shrink-0" />}
                  <span className="text-sm font-bold truncate text-white">{brand.name}</span>
                  <span className="text-gray-500 text-xs shrink-0">({brandModels.length})</span>
                </button>
                <CatActions cat={brand} />
              </div>

              {isExpBrand && (
                <div className="ml-3">
                  {brandModels.map(model => {
                    const isExpModel = !!expandedModels[model.id];
                    return (
                      <div key={model.id}>
                        {/* Ligne Modèle */}
                        <div className="group flex items-center gap-2 pl-5 pr-2 py-1.5 mx-1 rounded-xl hover:bg-gray-700 cursor-pointer transition-all">
                          <button onClick={() => toggleModel(model.id)} className="flex-1 flex items-center gap-1.5 text-left min-w-0">
                            {isExpModel
                              ? <ChevronDown size={10} className="text-gray-500 shrink-0" />
                              : <ChevronRight size={10} className="text-gray-500 shrink-0" />}
                            <span className="text-sm truncate text-gray-200">{model.name}</span>
                          </button>
                          <CatActions cat={model} />
                        </div>

                        {isExpModel && (
                          <div className="ml-3">
                            {partTypes.map(pt => (
                              <div key={pt.id}
                                onClick={() => onOpenCatalog?.({
                                  initialTab: getTabFromPartName(pt.name),
                                  initialBrand: brand.name,
                                  initialSearch: model.name,
                                })}
                                title={`Catalogue : ${brand.name} ${model.name} — ${pt.name}`}
                                className="group flex items-center gap-2 pl-6 pr-2 py-1.5 mx-1 rounded-xl cursor-pointer transition-all text-sm text-gray-400 hover:bg-amber-500/10 hover:text-amber-400">
                                <span className="text-gray-600 group-hover:text-amber-500">└</span>
                                <span className="truncate flex-1">{pt.name}</span>
                                <BookOpen size={10} className="text-gray-600 group-hover:text-amber-400 shrink-0" />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Ajouter un modèle sous cette marque */}
                  <AddCatButton type="model" onAdded={onCatsRefresh} brands={[brand]} defaultBrandId={brand.id} compact />
                </div>
              )}
            </div>
          );
        })}

        {/* Ajouter marque / catégorie */}
        <div className="px-2 pt-2 space-y-1">
          <AddCatButton type="brand" onAdded={onCatsRefresh} compact />
          <AddCatButton type="part_type" onAdded={onCatsRefresh} compact />
        </div>
      </div>
    </div>
  );
};

// ── AddCatButton ──────────────────────────────────────────────────────────────
const AddCatButton = ({ type, onAdded, brands = [], defaultBrandId = '', compact = false }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState(defaultBrandId);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      const body = { name: name.trim(), type };
      if (type === 'model' && parentId) body.parent_id = parentId;
      const r = await fetch(`${API_URL}/api/pricing/categories`, { method: 'POST', headers: authHdr(), body: JSON.stringify(body) });
      if (!r.ok) throw new Error((await r.json()).detail);
      toast.success('Ajouté !'); setOpen(false); setName(''); setParentId(defaultBrandId);
      onAdded();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (!open) return (
    <button onClick={() => setOpen(true)}
      className={`flex items-center gap-1 py-1.5 text-gray-400 hover:text-amber-500 w-full transition-colors text-xs ${compact ? 'pl-3' : 'pl-5 pr-3'}`}>
      <Plus size={10} /> Ajouter {TYPE_LABELS[type].toLowerCase()}
    </button>
  );

  return (
    <div className="mx-2 mt-1 p-2 bg-gray-800 rounded-xl border border-gray-700 space-y-1.5">
      {type === 'model' && brands.length > 0 && !defaultBrandId && (
        <select value={parentId} onChange={e => setParentId(e.target.value)}
          className="w-full bg-gray-700 border border-gray-600 text-gray-200 text-xs rounded-lg px-2 py-1.5 focus:outline-none">
          <option value="">Marque parente...</option>
          {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      )}
      <input autoFocus value={name} onChange={e => setName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setOpen(false); }}
        placeholder={`Nom ${TYPE_LABELS[type].toLowerCase()}...`}
        className="w-full bg-gray-700 border border-gray-600 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-500 placeholder-gray-500" />
      <div className="flex gap-1">
        <button onClick={save} disabled={saving}
          className="flex-1 bg-amber-500 hover:bg-amber-400 text-white text-xs font-bold py-1.5 rounded-lg">
          {saving ? '...' : 'Ajouter'}
        </button>
        <button onClick={() => setOpen(false)} className="px-2 bg-gray-700 text-gray-400 rounded-lg text-xs hover:bg-gray-600">✕</button>
      </div>
    </div>
  );
};

// ── RIGHT SUMMARY PANEL ───────────────────────────────────────────────────────
const InlineSummary = ({ result, quality, achat, qualityMap }) => {
  const q = qualityMap?.[quality];
  return (
    <div className="bg-gray-900 border border-gray-700 rounded-2xl overflow-hidden shadow-lg">
      <div className="px-4 py-2.5 border-b border-gray-700 flex items-center gap-2 bg-gray-800">
        <TrendingUp size={14} className="text-amber-500" />
        <span className="text-white text-sm font-bold">Résumé en direct</span>
        {q && <span className="ml-auto text-sm">{q.icon} <span className="text-gray-300">{q.label}</span></span>}
      </div>
      {!result ? (
        <div className="flex flex-col items-center justify-center py-6 text-gray-500">
          <Calculator size={24} className="mb-1.5" /><span className="text-sm">Saisissez un prix d'achat</span>
        </div>
      ) : (
        <div className="p-3 space-y-2">
          {/* Key metrics row */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-3 text-center">
              <p className="text-gray-400 text-xs mb-0.5">Achat HT</p>
              <p className="text-gray-100 font-bold text-sm">{fmt(parseFloat(achat) || 0)}</p>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-3 text-center">
              <p className="text-amber-400 text-xs mb-0.5 font-semibold">Vente TTC</p>
              <p className="text-amber-400 font-black text-lg">{fmt(result.pvTTC)}</p>
            </div>
            <div className={`rounded-xl p-3 text-center border ${result.tm >= 55 ? 'bg-green-500/10 border-green-500/40' : result.tm >= 35 ? 'bg-yellow-500/10 border-yellow-500/40' : 'bg-red-500/10 border-red-500/40'}`}>
              <p className={`text-xs mb-0.5 ${result.tm >= 55 ? 'text-green-400' : result.tm >= 35 ? 'text-yellow-400' : 'text-red-400'}`}>Marge</p>
              <p className={`font-black text-lg ${margeColor(result.tm)}`}>{fmtP(result.tm)}</p>
            </div>
          </div>
          {/* Details */}
          <div className="divide-y divide-gray-700 border border-gray-700 rounded-xl overflow-hidden">
            {[
              { label: 'Vente HT', val: fmt(result.pvHT), color: 'text-gray-200' },
              { label: 'Marge brute', val: fmt(result.mb), color: 'text-green-400' },
              ...(result.conc ? [{ label: 'Concurrent', val: fmt(result.conc), color: 'text-gray-400' }] : []),
              ...(result.ecart != null ? [{ label: 'Écart marché', val: (result.ecart > 0 ? '+' : '') + fmt(result.ecart), color: result.ecart > 0 ? 'text-red-400' : 'text-green-400' }] : []),
            ].map((row, i) => (
              <div key={i} className={`flex items-center justify-between px-3 py-2 ${i % 2 === 0 ? 'bg-gray-800/50' : 'bg-gray-900'}`}>
                <span className="text-gray-400 text-sm">{row.label}</span>
                <span className={`text-sm font-semibold ${row.color}`}>{row.val}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── SelectWithAdd (standalone — must be outside PricingCalcPanel to avoid remounting) ─
const SelectWithAdd = ({ label, value, onChange, options, type, disabled, inlineAdd, setInlineAdd, brandId, onInlineAdded }) => (
  <div>
    <label className="text-gray-300 text-xs block mb-1 font-medium">{label}</label>
    <div className="flex gap-1">
      <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
        className="flex-1 bg-gray-800 border border-gray-600 text-white text-xs rounded-xl px-2 py-2 focus:outline-none focus:border-amber-500 disabled:opacity-50 min-w-0">
        <option value="">— {disabled ? '(choisir marque)' : 'Aucun'} —</option>
        {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
      <button onClick={() => setInlineAdd(inlineAdd === type ? null : type)} title={`Ajouter ${TYPE_LABELS[type].toLowerCase()}`}
        className={`shrink-0 w-7 h-7 mt-0.5 flex items-center justify-center rounded-lg border transition-all
          ${inlineAdd === type ? 'bg-amber-500 border-amber-400 text-white' : 'bg-gray-700 border-gray-600 text-gray-400 hover:border-amber-500 hover:text-amber-500'}`}>
        <Plus size={12} />
      </button>
    </div>
    {inlineAdd === type && (
      <InlineAdd type={type} brandId={brandId} onAdded={onInlineAdded} onCancel={() => setInlineAdd(null)} />
    )}
  </div>
);

// ── CALCULATOR (Center) ───────────────────────────────────────────────────────
const PricingCalcPanel = ({ config, categories, selectedProduct, onProductSaved, onCatsRefresh, liveInputs, setLiveInputs, onClearProduct, importedPrice, onImportedPriceConsumed, qualityTypes = [], onConfigUpdated }) => {
  // Form state
  const [achat, setAchat]               = useState('');
  const [quality, setQuality]           = useState('premium');
  const [complexity, setComplexity]     = useState('simple');
  const [concurrence, setConcurrence]   = useState('');
  const [notes, setNotes]               = useState('');
  const [brandId, setBrandId]           = useState('');
  const [modelId, setModelId]           = useState('');
  const [partId, setPartId]             = useState('');
  const [customPrice, setCustomPrice]  = useState('');
  // Client profile (ticket)
  const [clientName, setClientName]     = useState('');
  const [repairNum, setRepairNum]       = useState('');
  const [pickupDate, setPickupDate]     = useState('');
  const [clientPhone, setClientPhone]   = useState('');
  const [pinCode, setPinCode]           = useState('');

  // ── Infos vente produit ──
  const [storage, setStorage]           = useState('');
  const [saleCondition, setSaleCondition] = useState('');
  const [batteryPct, setBatteryPct]     = useState('');
  const [paymentText, setPaymentText]   = useState('Paiement en 3X ou 4X');
  const [showSalePanel, setShowSalePanel] = useState(false);
  const [showClientPanel, setShowClientPanel] = useState(false);

  // UI state
  const [saving, setSaving]             = useState(false);
  const [copied, setCopied]             = useState(false);
  const [showConc, setShowConc]         = useState(false);
  const [priceMode, setPriceMode]       = useState('saisir'); // 'saisir' | 'importer'
  const [supplierUrl, setSupplierUrl]   = useState(() => localStorage.getItem('phonelab_supplier_url') || '');
  const [importPriceRaw, setImportPriceRaw] = useState('');
  const [searchVal, setSearchVal]       = useState('');
  const [suggestions, setSuggestions]   = useState([]);
  const [showSugg, setShowSugg]         = useState(false);
  const [inlineAdd, setInlineAdd]       = useState(null); // 'brand'|'model'|'part_type'
  const [variants, setVariants]         = useState([]);
  const [showPrintConfig, setShowPrintConfig] = useState(false);
  const [labelPreview, setLabelPreview] = useState(null); // { type: 'repair'|'sale', initial: {} }
  const [variantsLoading, setVariantsLoading] = useState(false);

  // ── Quality Manager state
  const [showQualMgr, setShowQualMgr]   = useState(false);
  const [editableQt, setEditableQt]     = useState([]);
  const [savingQt, setSavingQt]         = useState(false);
  const [addingQ, setAddingQ]           = useState(false);
  const [newQ, setNewQ]                 = useState({ key: '', label: '', icon: '', warranty: '' });

  // ── Marge custom calculator state (violet)
  const [margeCalc, setMargeCalc]       = useState({ prixHT: '', marge: '' });

  const searchRef = useRef(null);
  const suggRef = useRef(null);

  const brands    = categories.filter(c => c.type === 'brand');
  const models    = categories.filter(c => c.type === 'model' && (!brandId || c.parent_id === brandId));
  const partTypes = categories.filter(c => c.type === 'part_type');

  const brandLabel = brands.find(b => b.id === brandId)?.name || '';
  const modelLabel = categories.find(m => m.id === modelId)?.name || '';
  const partLabel  = partTypes.find(p => p.id === partId)?.name || '';

  const isEditMode = !!selectedProduct;

  // ── Quality Manager helpers
  const openQualMgr = () => {
    const qt = qualityTypes.length > 0
      ? qualityTypes
      : Object.entries(QUALITY_MAP).map(([k, v]) => ({ key: k, ...v }));
    setEditableQt(qt.map(q => ({ ...q })));
    setShowQualMgr(true);
    setAddingQ(false);
    setNewQ({ key: '', label: '', icon: '', warranty: '' });
  };

  const saveQualTypes = async () => {
    setSavingQt(true);
    try {
      const current = config || {};
      const qc = { ...(current.quality_coefficients || {}) };
      // Ajouter coefficient par défaut pour les nouvelles qualités
      editableQt.forEach(q => { if (!qc[q.key]) qc[q.key] = 2.3; });
      // Nettoyer les clés supprimées
      const validKeys = new Set(editableQt.map(q => q.key));
      Object.keys(qc).forEach(k => { if (!validKeys.has(k)) delete qc[k]; });
      const body = { ...current, quality_types: editableQt, quality_coefficients: qc };
      const r = await fetch(`${API_URL}/api/pricing/config`, { method: 'PUT', headers: authHdr(), body: JSON.stringify(body) });
      if (!r.ok) throw new Error();
      toast.success('Qualités sauvegardées !');
      setShowQualMgr(false);
      onConfigUpdated?.();
    } catch { toast.error('Erreur lors de la sauvegarde'); }
    finally { setSavingQt(false); }
  };

  const handleAddQuality = () => {
    if (!newQ.key.trim() || !newQ.label.trim()) { toast.error('Clé et libellé requis'); return; }
    const key = newQ.key.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (editableQt.find(q => q.key === key)) { toast.error('Cette clé existe déjà'); return; }
    setEditableQt(prev => [...prev, { key, label: newQ.label.trim(), icon: newQ.icon || '🔧', warranty: newQ.warranty || '' }]);
    setNewQ({ key: '', label: '', icon: '', warranty: '' });
    setAddingQ(false);
  };

  const deleteQuality = (key) => {
    if (editableQt.length <= 1) { toast.error('Il faut au moins une qualité'); return; }
    setEditableQt(prev => prev.filter(q => q.key !== key));
  };

  // Consume imported price from supplier panel
  useEffect(() => {
    if (importedPrice != null && importedPrice > 0) {
      setAchat(importedPrice.toString());
      setPriceMode('saisir');
      onImportedPriceConsumed?.();
      toast.success(`Prix fournisseur ${importedPrice.toFixed(2)} € HT appliqué !`);
    }
  }, [importedPrice]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fill from selectedProduct (from left panel click)
  useEffect(() => {
    if (!selectedProduct) {      // Reset all fields when clearing selection
      setAchat('');
      setQuality('premium');
      setComplexity('simple');
      setNotes('');
      setBrandId('');
      setModelId('');
      setPartId('');
      setCustomPrice('');
      setConcurrence('');
      setSearchVal('');
      setSuggestions([]);
      setShowSugg(false);
      return;
    }
    setAchat(selectedProduct.prix_achat_ht?.toString() || '');
    setQuality(selectedProduct.quality || 'premium');
    setComplexity(selectedProduct.complexity || 'simple');
    setNotes(selectedProduct.notes || '');
    setBrandId(selectedProduct.brand_id || '');
    setModelId(selectedProduct.model_id || '');
    setPartId(selectedProduct.part_type_id || '');
    setCustomPrice(selectedProduct.prix_vente_custom?.toString() || '');
    setSearchVal('');
    setSuggestions([]);
    setShowSugg(false);
  }, [selectedProduct]);

  // Broadcast live values to parent
  useEffect(() => {
    setLiveInputs({ achat, quality, complexity, concurrence, customPrice });
  }, [achat, quality, complexity, concurrence, customPrice]);

  // Save supplier URL to localStorage
  useEffect(() => {
    if (supplierUrl) localStorage.setItem('phonelab_supplier_url', supplierUrl);
  }, [supplierUrl]);

  // Load quality variants when device+part are selected
  useEffect(() => {
    if (!brandId || !partId) { setVariants([]); return; }
    const load = async () => {
      setVariantsLoading(true);
      try {
        const params = new URLSearchParams({ brand_id: brandId, part_type_id: partId });
        if (modelId) params.set('model_id', modelId);
        const r = await fetch(`${API_URL}/api/pricing/products/variants?${params}`, { headers: authHdr() });
        if (r.ok) setVariants(await r.json());
        else setVariants([]);
      } catch { setVariants([]); }
      finally { setVariantsLoading(false); }
    };
    load();
  }, [brandId, modelId, partId]);

  // Product search suggestions
  const fetchSuggestions = useCallback(async (q) => {
    if (!q.trim()) { setSuggestions([]); return; }
    try {
      const r = await fetch(`${API_URL}/api/pricing/products?search=${encodeURIComponent(q)}&limit=12`, { headers: authHdr() });
      if (r.ok) setSuggestions(await r.json());
    } catch { setSuggestions([]); }
  }, []);

  // Live result — computed inline for InlineSummary
  const result = computeLive(achat, quality, complexity, concurrence, config, customPrice);

  const handleSearchChange = (val) => {
    setSearchVal(val);
    setShowSugg(true);
    fetchSuggestions(val);
  };

  const selectSuggestion = (p) => {
    onProductSaved && onProductSaved('select', p); // tell parent to set selectedProduct
    setShowSugg(false); setSearchVal('');
  };

  // Click outside to close suggestions
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target) && suggRef.current && !suggRef.current.contains(e.target)) {
        setShowSugg(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleInlineAdded = async (cat) => {
    setInlineAdd(null);
    await onCatsRefresh(); // Wait for categories to refresh THEN set the id
    if (cat.type === 'brand')     { setBrandId(cat.id); setModelId(''); }
    if (cat.type === 'model')     { setModelId(cat.id); }
    if (cat.type === 'part_type') { setPartId(cat.id); }
  };

  const getBodyForSave = () => ({
    prix_achat_ht: parseFloat(achat),
    quality, complexity,
    brand_id: brandId, brand_label: brandLabel,
    model_id: modelId, model_label: modelLabel,
    part_type_id: partId, part_label: partLabel,
    concurrence_ttc: concurrence ? parseFloat(concurrence) : null,
    notes,
    prix_vente_custom: customPrice ? parseFloat(customPrice) : null,
    supplier_url: supplierUrl
  });

  const saveToHistory = async () => {
    if (!achat || parseFloat(achat) <= 0) { toast.error("Saisissez un prix d'achat valide"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/pricing/calculate`, { method: 'POST', headers: authHdr(), body: JSON.stringify(getBodyForSave()) });
      if (!r.ok) throw new Error((await r.json()).detail);
      toast.success('Calcul enregistré dans l\'historique !');
      onProductSaved?.('history');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const resetForm = () => {
    setBrandId(''); setModelId(''); setPartId('');
    setAchat(''); setCustomPrice(''); setNotes('');
    setQuality('premium'); setComplexity('simple');
  };

  const createProduct = async () => {
    if (!achat || parseFloat(achat) <= 0) { toast.error("Saisissez un prix d'achat valide"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/pricing/products`, { method: 'POST', headers: authHdr(), body: JSON.stringify(getBodyForSave()) });
      if (!r.ok) throw new Error((await r.json()).detail);
      toast.success('Nouvelle fiche créée !');
      onProductSaved?.('product');
      resetForm();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const updateProduct = async () => {
    if (!achat || parseFloat(achat) <= 0) { toast.error("Saisissez un prix d'achat valide"); return; }
    if (!selectedProduct?.id) { toast.error("Aucune fiche sélectionnée"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/pricing/products/${selectedProduct.id}`, { method: 'PUT', headers: authHdr(), body: JSON.stringify(getBodyForSave()) });
      if (!r.ok) throw new Error((await r.json()).detail);
      toast.success('Fiche produit modifiée !');
      onProductSaved?.('product');
      resetForm();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const duplicateAsNewSheet = () => {
    onClearProduct?.();
    toast.info('Mode création — modifiez la qualité et le prix, puis cliquez "Créer fiche"');
  };

  const copyForClient = () => {
    if (!achat) { toast.error('Saisissez un prix d\'achat'); return; }
    const result = computeLive(achat, quality, complexity, concurrence, config, customPrice);
    if (!result) return;
    const q = QUALITY_MAP[quality];
    const deviceLabel = [brandLabel, modelLabel, partLabel].filter(Boolean).join(' ') || 'votre appareil';
    const text = `💬 Devis PhoneLab 69\n📱 ${deviceLabel}\n💰 Prix TTC : ${result.pvTTC.toFixed(0)}€\n🏅 ${q.label} — Garantie ${q.warranty}\n✅ Intervention rapide\n📍 Villeurbanne (T6 Gaston Berger)\n📞 04 XX XX XX XX`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2500);
      toast.success('Devis copié !');
    }).catch(() => toast.error('Erreur copie — vérifiez les permissions'));
  };

  const handlePrint = () => {
    const result = computeLive(achat, quality, complexity, concurrence, config, customPrice);
    if (!result) { toast.error('Saisissez un prix d\'achat'); return; }
    printPOS({ brand: brandLabel, model: modelLabel, part: partLabel, quality, complexity, result, notes,
               clientName, repairNum, pickupDate });
  };

  const handlePrintLabel = () => {
    const result = computeLive(achat, quality, complexity, concurrence, config, customPrice);
    if (!result) { toast.error('Saisissez un prix d\'achat'); return; }
    setLabelPreview({
      type: 'repair',
      initial: { brand: brandLabel, model: modelLabel, part: partLabel, notes, pinCode,
                 clientName, clientPhone, repairNum, priceTTC: Math.round(result.pvTTC * 100) / 100 },
    });
  };

  const handlePrintSaleLabel = () => {
    const result = computeLive(achat, quality, complexity, concurrence, config, customPrice);
    // Ouvre directement la modal — comme dans le catalogue — même sans prix calculé.
    // L'utilisateur peut saisir le prix directement dans le champ du modal.
    const priceTTC = result
      ? Math.round(result.pvTTC * 100) / 100
      : (customPrice ? parseFloat(customPrice) : undefined);
    setLabelPreview({
      type: 'sale',
      initial: { brand: brandLabel, model: modelLabel, storage, saleCondition, batteryPct, paymentText, priceTTC },
    });
  };

  const generateRepairNum = () => {
    const d = new Date();
    const key = `phonelab_seq_${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}`;
    const seq = (parseInt(localStorage.getItem(key) || '0') + 1);
    localStorage.setItem(key, String(seq));
    const dateStr = `${String(d.getDate()).padStart(2,'0')}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getFullYear()).slice(-2)}`;
    return `REP-${dateStr}-${String(seq).padStart(3, '0')}`;
  };

  const importFound = () => {
    if (!importPriceRaw) { toast.error('Saisissez le prix trouvé'); return; }
    setAchat(importPriceRaw);
    setPriceMode('saisir');
    toast.success(`Prix ${importPriceRaw}€ HT importé !`);
  };

  return (
    <>
    <div className="flex-1 bg-gray-900 border border-gray-700 rounded-2xl overflow-hidden flex flex-col min-w-0 shadow-lg">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-700 shrink-0 bg-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isEditMode ? (
              <>
                <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                <span className="text-amber-400 text-sm font-semibold">Mode édition</span>
                <span className="text-gray-500 text-xs truncate max-w-32">
                  {[selectedProduct.brand_label, selectedProduct.model_label, selectedProduct.part_label].filter(Boolean).join(' · ')}
                </span>
              </>
            ) : (
              <>
                <Calculator size={15} className="text-amber-500" />
                <span className="text-white font-semibold text-sm">Calculateur</span>
              </>
            )}
          </div>
          {isEditMode && (
            <button onClick={onClearProduct}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-white bg-gray-700 hover:bg-gray-600 border border-gray-600 px-2 py-1.5 rounded-lg transition-all">
              <X size={11} /> Nouvelle fiche
            </button>
          )}
        </div>

        {/* Search / Autocomplete */}
        <div className="relative mt-2">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input ref={searchRef} value={searchVal} onChange={e => handleSearchChange(e.target.value)}
            onFocus={() => { if (searchVal) setShowSugg(true); }}
            placeholder="Rechercher un produit existant..."
            data-testid="product-search"
            className="w-full bg-gray-800 border border-gray-600 text-white text-xs rounded-xl pl-8 pr-3 py-2 focus:outline-none focus:border-amber-400 placeholder-gray-500" />
          {searchVal && <button onClick={() => { setSearchVal(''); setSuggestions([]); setShowSugg(false); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"><X size={10} /></button>}
          {showSugg && suggestions.length > 0 && (
            <div ref={suggRef} className="absolute top-full mt-1 left-0 right-0 bg-gray-800 border border-gray-600 rounded-xl shadow-xl z-50 overflow-hidden max-h-64 overflow-y-auto">
              {suggestions.map(p => {
                const q = QUALITY_MAP[p.quality];
                const name = [p.brand_label, p.model_label, p.part_label].filter(Boolean).join(' · ') || p.quality;
                return (
                  <button key={p.id} onClick={() => selectSuggestion(p)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-gray-700 transition-colors text-left border-b border-gray-700 last:border-0">
                    <span className="text-base shrink-0">{q?.icon || '?'}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs font-semibold truncate">{name}</p>
                      <p className="text-gray-400 text-xs">{q?.label || p.quality}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-amber-400 text-xs font-bold">{fmt(p.prix_vente_ttc)}</p>
                      <p className="text-gray-500 text-xs">achat {fmt(p.prix_achat_ht)}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="p-4 overflow-y-auto flex-1 space-y-3">
        {/* Device selectors + Quality */}
        <div className="grid grid-cols-2 gap-2">
          <SelectWithAdd label="Marque" value={brandId} onChange={(v) => { setBrandId(v); setModelId(''); }} options={brands} type="brand"
            inlineAdd={inlineAdd} setInlineAdd={setInlineAdd} brandId={brandId} onInlineAdded={handleInlineAdded} />
          <SelectWithAdd label="Modèle" value={modelId} onChange={setModelId} options={models} type="model" disabled={!brandId}
            inlineAdd={inlineAdd} setInlineAdd={setInlineAdd} brandId={brandId} onInlineAdded={handleInlineAdded} />
          <SelectWithAdd label="Catégorie" value={partId} onChange={setPartId} options={partTypes} type="part_type"
            inlineAdd={inlineAdd} setInlineAdd={setInlineAdd} brandId={brandId} onInlineAdded={handleInlineAdded} />
          {/* Qualité — dropdown compact + bouton gérer */}
          <div className="col-span-2">
            <div className="flex items-center justify-between mb-1">
              <label className="text-gray-300 text-xs font-medium">Qualité</label>
              <button onClick={() => showQualMgr ? setShowQualMgr(false) : openQualMgr()}
                data-testid="manage-qualities-btn"
                className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-lg border transition-all
                  ${showQualMgr ? 'bg-amber-500 text-white border-amber-400' : 'text-gray-400 border-gray-600 hover:border-amber-400 hover:text-amber-400'}`}>
                <Settings size={10} /> Gérer
              </button>
            </div>
            <select value={quality} onChange={e => setQuality(e.target.value)}
              data-testid="quality-select"
              className="w-full bg-gray-800 border border-gray-600 text-white text-xs rounded-xl px-2 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500">
              {(qualityTypes.length > 0 ? qualityTypes : Object.entries(QUALITY_MAP).map(([k, v]) => ({ key: k, ...v }))).map(q => (
                <option key={q.key} value={q.key}>{q.icon} {q.label}</option>
              ))}
            </select>

            {/* ── Quality Manager Panel ── */}
            {showQualMgr && (
              <div className="mt-2 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 space-y-2" data-testid="quality-manager-panel">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <p className="text-amber-400 font-bold text-xs flex items-center gap-1">
                    <Settings size={11} /> Gestion des qualités
                  </p>
                  <button onClick={saveQualTypes} disabled={savingQt}
                    data-testid="save-qualities-btn"
                    className="flex items-center gap-1 text-xs text-white bg-green-600 hover:bg-green-500 disabled:opacity-50 px-2 py-1 rounded-lg transition-all">
                    {savingQt ? '...' : <><Save size={10} /> Sauvegarder</>}
                  </button>
                </div>

                {/* Existing qualities list */}
                <div className="space-y-1 max-h-52 overflow-y-auto">
                  {editableQt.map((q, i) => (
                    <div key={q.key} className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-xl px-2.5 py-2">
                      {/* Emoji/Icon */}
                      <input value={q.icon}
                        onChange={e => setEditableQt(prev => prev.map((x, j) => j === i ? { ...x, icon: e.target.value } : x))}
                        className="w-9 text-center text-base bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-400 shrink-0 py-0.5 text-white"
                        maxLength={2} title="Icône emoji" />
                      {/* Label */}
                      <input value={q.label}
                        onChange={e => setEditableQt(prev => prev.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                        placeholder="Libellé..."
                        className="flex-1 bg-transparent text-white text-xs font-semibold focus:outline-none border-b border-transparent focus:border-amber-400 min-w-0 py-0.5" />
                      {/* Warranty */}
                      <input value={q.warranty || ''}
                        onChange={e => setEditableQt(prev => prev.map((x, j) => j === i ? { ...x, warranty: e.target.value } : x))}
                        placeholder="Garantie"
                        className="w-20 text-gray-400 text-xs focus:outline-none border-b border-transparent focus:border-gray-500 bg-transparent shrink-0 py-0.5" />
                      {/* Key badge */}
                      <span className="text-gray-600 text-xs font-mono shrink-0 hidden sm:block">{q.key}</span>
                      {/* Delete */}
                      <button onClick={() => deleteQuality(q.key)}
                        className="p-1 text-gray-600 hover:text-red-400 transition-colors shrink-0">
                        <Trash2 size={11} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add new quality */}
                {addingQ ? (
                  <div className="bg-gray-800 border border-blue-500/30 rounded-xl p-2.5 space-y-2">
                    <p className="text-blue-400 text-xs font-bold">Nouvelle qualité</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      <input value={newQ.key} onChange={e => setNewQ(n => ({ ...n, key: e.target.value }))}
                        placeholder="Clé unique (ex: ultra_oled)"
                        className="col-span-2 bg-gray-700 border border-gray-600 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 placeholder-gray-500" />
                      <input value={newQ.label} onChange={e => setNewQ(n => ({ ...n, label: e.target.value }))}
                        placeholder="Libellé affiché"
                        className="bg-gray-700 border border-gray-600 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 placeholder-gray-500" />
                      <input value={newQ.icon} onChange={e => setNewQ(n => ({ ...n, icon: e.target.value }))}
                        placeholder="Icône 🔧" maxLength={2}
                        className="bg-gray-700 border border-gray-600 text-white text-sm text-center rounded-lg px-2 py-1.5 focus:outline-none placeholder-gray-500" />
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={handleAddQuality}
                        data-testid="confirm-add-quality-btn"
                        className="flex-1 flex items-center justify-center gap-1 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold py-1.5 rounded-lg transition-all">
                        <Plus size={11} /> Ajouter
                      </button>
                      <button onClick={() => { setAddingQ(false); setNewQ({ key: '', label: '', icon: '', warranty: '' }); }}
                        className="px-3 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs rounded-lg">✕</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setAddingQ(true)}
                    data-testid="add-quality-btn"
                    className="w-full flex items-center justify-center gap-1.5 border-2 border-dashed border-amber-500/30 text-amber-500 hover:border-amber-400 hover:text-amber-400 rounded-xl py-2 text-xs font-semibold transition-all">
                    <Plus size={11} /> Nouvelle qualité
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Complexité ── */}
        <div>
          <label className="text-gray-300 text-xs font-medium block mb-1.5">Complexité</label>
          <div className="flex gap-1.5">
            {Object.entries(COMPLEXITY).map(([key, c]) => (
              <button key={key} onClick={() => setComplexity(key)} data-testid={`complexity-${key}`}
                className={`flex-1 flex flex-col items-center py-2 rounded-xl border-2 text-center transition-all active:scale-[0.97]
                  ${complexity === key
                    ? 'border-amber-400 bg-amber-500/10 text-amber-400'
                    : 'border-gray-600 bg-gray-800 hover:border-gray-500 text-gray-300 hover:text-white'}`}>
                <span className="text-xs font-bold">{c.label}</span>
                <span className="font-mono mt-0.5 text-gray-500" style={{ fontSize: '10px' }}>{c.coef}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Variantes existantes pour ce produit ── */}
        {brandId && partId && (
          <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700 bg-gray-700/50">
              <p className="text-xs font-semibold text-gray-300">
                {variantsLoading ? 'Chargement des variantes...' : `Variantes enregistrées (${variants.length})`}
              </p>
              <p className="text-xs text-gray-500">Cliquez pour charger dans le calculateur</p>
            </div>
            {!variantsLoading && variants.length === 0 && (
              <p className="text-gray-500 text-xs px-3 py-2.5 italic">Aucune variante — créez la première ci-dessous</p>
            )}
            {variants.map(v => {
              const q = QUALITY_MAP[v.quality] || { icon: '?', label: v.quality, color: 'text-gray-500' };
              const isActive = selectedProduct?.id === v.id;
              return (
                <button key={v.id} onClick={() => onProductSaved?.('select', v)}
                  data-testid={`variant-row-${v.quality}`}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 border-b border-gray-700 last:border-0 transition-colors text-left
                    ${isActive ? 'bg-amber-500/10 border-amber-500/30' : 'hover:bg-gray-700'}`}>
                  <span className="text-base">{q.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-200">{q.label}</p>
                    <p className="text-gray-500 text-xs">achat {fmt(v.prix_achat_ht)} HT</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-amber-400 text-xs font-bold">{fmt(v.prix_vente_ttc)}</p>
                    <p className="text-gray-500 text-xs">marge {v.taux_marge?.toFixed(0)}%</p>
                  </div>
                  {isActive && <div className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />}
                </button>
              );
            })}
          </div>
        )}

        {/* Prix achat HT */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-gray-300 text-xs font-medium">Prix d'achat HT (€) *</label>
            <div className="flex bg-gray-700 rounded-lg p-0.5 gap-0.5">
              <button onClick={() => setPriceMode('saisir')} data-testid="mode-saisir"
                className={`text-xs px-2 py-1 rounded-md transition-all ${priceMode === 'saisir' ? 'bg-gray-500 text-white shadow-sm' : 'text-gray-400 hover:text-gray-200'}`}>
                Saisir
              </button>
              <button onClick={() => setPriceMode('importer')} data-testid="mode-importer"
                className={`text-xs px-2 py-1 rounded-md transition-all flex items-center gap-1 ${priceMode === 'importer' ? 'bg-blue-500 text-white shadow-sm' : 'text-gray-400 hover:text-gray-200'}`}>
                <Download size={11} /> Importer
              </button>
            </div>
          </div>

          {priceMode === 'saisir' ? (
            <div className="relative">
              <input type="number" value={achat} onChange={e => setAchat(e.target.value)}
                placeholder="Ex : 45.00" step="0.5" min="0" data-testid="prix-achat-input"
                className="w-full bg-gray-800 border border-gray-600 text-white text-xl font-black rounded-xl px-4 py-3 pr-14 focus:outline-none focus:border-amber-400 placeholder-gray-600 transition-colors" />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-sm">€ HT</span>
            </div>
          ) : (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 space-y-2">
              <p className="text-blue-400 text-xs font-semibold">Importer depuis votre fournisseur</p>
              <div className="flex gap-2">
                <input value={supplierUrl} onChange={e => setSupplierUrl(e.target.value)}
                  placeholder="URL de votre espace fournisseur..."
                  data-testid="supplier-url-input"
                  className="flex-1 bg-gray-800 border border-gray-600 text-white text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-blue-500 placeholder-gray-600 min-w-0" />
                <button onClick={() => supplierUrl ? window.open(supplierUrl, '_blank') : toast.error('Saisissez une URL')}
                  data-testid="open-supplier-btn"
                  className="shrink-0 flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold px-3 py-2 rounded-xl transition-all">
                  <ExternalLink size={12} /> Ouvrir
                </button>
              </div>
              <p className="text-gray-500 text-xs">Une fois le prix trouvé, saisissez-le ici :</p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input type="number" value={importPriceRaw} onChange={e => setImportPriceRaw(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') importFound(); }}
                    placeholder="Prix trouvé (€ HT)..." step="0.01" min="0"
                    data-testid="import-price-input"
                    className="w-full bg-gray-800 border border-gray-600 text-white text-sm rounded-xl px-3 py-2 pr-10 focus:outline-none focus:border-green-500 placeholder-gray-600" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">HT</span>
                </div>
                <button onClick={importFound} data-testid="import-price-btn"
                  className="shrink-0 bg-green-600 hover:bg-green-500 text-white text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1 transition-all">
                  <Check size={12} /> Importer
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Prix personnalisé TTC (admin) ── */}
        <div>
          <label className="text-gray-300 text-xs font-semibold block mb-1.5">Prix personnalisé TTC (admin)</label>
          {result && <p className="text-gray-500 text-xs mb-1">Suggestion calculée : <span className="text-amber-400 font-semibold">{fmt(result.suggested_ttc)}</span></p>}
          <div className="relative">
            <input type="number" value={customPrice}
              onChange={e => { setCustomPrice(e.target.value); setLiveInputs?.(p => ({ ...p, customPrice: e.target.value })); }}
              placeholder="Prix custom TTC..." step="0.5" min="0" data-testid="custom-price-input"
              className="w-full bg-gray-800 border border-gray-600 text-white text-sm rounded-xl px-3 py-2.5 pr-12 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 placeholder-gray-600" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs font-bold">€ TTC</span>
          </div>
          {customPrice && parseFloat(customPrice) > 0 && (
            <button onClick={() => { setCustomPrice(''); setLiveInputs?.(p => ({ ...p, customPrice: '' })); }}
              className="mt-1 w-full text-xs text-gray-500 hover:text-gray-300 flex items-center justify-center gap-1 py-1">
              <RefreshCw size={10} /> Revenir au suggéré
            </button>
          )}
        </div>

        {/* ── Marge custom — calculateur violet ── */}
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-3 space-y-2">
          <p className="text-purple-400 text-xs font-bold flex items-center gap-1.5">
            <Percent size={11} /> Marge custom → génère Prix Custom TTC
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-purple-400 text-[10px] font-bold block mb-1">Prix HT</label>
              <div className="relative">
                <input type="number"
                  value={margeCalc.prixHT}
                  onChange={e => setMargeCalc(p => ({ ...p, prixHT: e.target.value }))}
                  placeholder={achat || 'Ex: 45'}
                  className="w-full border border-purple-500/40 rounded-xl px-3 py-2 text-xs text-white bg-gray-800 focus:outline-none focus:ring-1 focus:ring-purple-400 font-mono placeholder-gray-600"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-[10px]">€</span>
              </div>
            </div>
            <div>
              <label className="text-purple-400 text-[10px] font-bold block mb-1">Marge HT €</label>
              <div className="relative">
                <input type="number"
                  value={margeCalc.marge}
                  onChange={e => setMargeCalc(p => ({ ...p, marge: e.target.value }))}
                  placeholder="Ex: 30" min="0"
                  className="w-full border border-purple-500/40 rounded-xl px-3 py-2 text-xs text-white bg-gray-800 focus:outline-none focus:ring-1 focus:ring-purple-400 font-mono placeholder-gray-600"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-[10px]">€</span>
              </div>
            </div>
          </div>
          {(() => {
            const ht      = parseFloat(margeCalc.prixHT || achat);
            const margeHT = parseFloat(margeCalc.marge);
            const tva     = 1 + (config?.tva_rate ?? 0.20);
            const venteHT = (!isNaN(ht) && ht > 0 && !isNaN(margeHT) && margeHT >= 0)
              ? Math.round((ht + margeHT) * 100) / 100
              : null;
            const ttc = venteHT ? Math.round(venteHT * tva * 100) / 100 : null;
            if (!ttc) return null;
            return (
              <div className="space-y-1">
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-700/50 rounded-lg border border-gray-600">
                  <span className="text-gray-400 text-[10px]">{fmt(ht)} achat + {fmt(margeHT)} marge = <strong className="text-gray-200">{fmt(venteHT)} HT</strong></span>
                </div>
              <div className="flex items-center justify-between bg-purple-500/20 border border-purple-500/40 rounded-xl px-3 py-2">
                <span className="text-purple-300 text-xs font-semibold">→ Prix Custom TTC</span>
                <div className="flex items-center gap-2">
                  <span className="font-black text-purple-200 text-base font-mono">{fmt(ttc)}</span>
                  <button
                    onClick={() => { setCustomPrice(String(ttc)); setLiveInputs?.(p => ({ ...p, customPrice: String(ttc) })); toast.success('Prix custom appliqué !'); }}
                    className="bg-purple-500 hover:bg-purple-600 text-white text-[10px] font-bold px-2 py-1 rounded-lg transition-all">
                    ✓ Appliquer
                  </button>
                </div>
              </div>
              </div>
            );
          })()}
        </div>

        {/* ── Résumé en direct ── */}
        <InlineSummary
          result={result}
          quality={quality}
          achat={achat}
          qualityMap={qualityTypes.length > 0
            ? Object.fromEntries(qualityTypes.map(q => [q.key, q]))
            : QUALITY_MAP}
        />

        {/* Prix concurrence */}
        <div>
          <button onClick={() => setShowConc(s => !s)} className="flex items-center gap-1.5 text-gray-400 hover:text-gray-200 text-xs transition-colors">
            {showConc ? <ChevronUp size={11} /> : <ChevronDown size={11} />} Prix concurrent (optionnel)
          </button>
          {showConc && (
            <div className="relative mt-1.5">
              <input type="number" value={concurrence} onChange={e => setConcurrence(e.target.value)}
                placeholder="Prix concurrent TTC €" step="0.5" min="0"
                className="w-full bg-gray-800 border border-gray-600 text-white text-sm rounded-xl px-3 py-2 pr-12 focus:outline-none focus:border-blue-400 placeholder-gray-600" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">TTC</span>
            </div>
          )}
        </div>

        {/* Notes */}
        <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Notes internes..." data-testid="notes-input"
          className="w-full bg-gray-800 border border-gray-600 text-white text-sm rounded-xl px-3 py-2 focus:outline-none focus:border-gray-400 placeholder-gray-600" />

        {/* ── Profil client pour ticket ── */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
          <button
            onClick={() => {
              const next = !showClientPanel;
              setShowClientPanel(next);
              if (next && !repairNum) setRepairNum(generateRepairNum());
            }}
            className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-gray-700 transition-colors">
            <div className="flex items-center gap-2">
              <span className="text-sm">👤</span>
              <span className="text-gray-300 text-xs font-semibold">Infos client (ticket)</span>
              {(clientName || repairNum || pickupDate) && (
                <span className="bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">●</span>
              )}
            </div>
            {showClientPanel
              ? <ChevronUp size={13} className="text-gray-500" />
              : <ChevronDown size={13} className="text-gray-500" />}
          </button>

          {showClientPanel && (
            <div className="px-3 pb-3 space-y-2 border-t border-gray-700">
              {/* Nom client */}
              <div className="pt-2">
                <label className="text-gray-400 text-[10px] font-bold block mb-1">Nom du client</label>
                <input type="text" value={clientName} onChange={e => setClientName(e.target.value)}
                  placeholder="Ex: Jean Dupont" data-testid="client-name-input"
                  className="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-500" />
              </div>

              {/* Téléphone + PIN côte à côte */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-gray-400 text-[10px] font-bold block mb-1">Téléphone client</label>
                  <input type="tel" value={clientPhone} onChange={e => setClientPhone(e.target.value)}
                    placeholder="0612345678" data-testid="client-phone-input"
                    className="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-500" />
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] font-bold block mb-1">PIN / Code déverrouillage</label>
                  <input type="text" value={pinCode} onChange={e => setPinCode(e.target.value)}
                    placeholder="Ex: 1234" data-testid="pin-code-input"
                    className="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-500 font-mono" />
                </div>
              </div>

              {/* N° réparation */}
              <div>
                <label className="text-gray-400 text-[10px] font-bold block mb-1">N° Réparation</label>
                <div className="flex gap-1.5">
                  <input type="text" value={repairNum} onChange={e => setRepairNum(e.target.value)}
                    placeholder="REP-010426-001" data-testid="repair-num-input"
                    className="flex-1 bg-gray-700 border border-gray-600 text-white text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-500 font-mono" />
                  <button onClick={() => setRepairNum(generateRepairNum())} title="Générer automatiquement"
                    className="shrink-0 px-3 py-2 bg-gray-600 hover:bg-gray-500 text-gray-300 text-xs rounded-xl border border-gray-500 transition-all font-mono">
                    +1
                  </button>
                </div>
              </div>

              {/* Date de récupération */}
              <div>
                <label className="text-gray-400 text-[10px] font-bold block mb-1">Date de récupération</label>
                <input type="datetime-local" value={pickupDate} onChange={e => setPickupDate(e.target.value)}
                  data-testid="pickup-date-input"
                  className="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 [color-scheme:dark]" />
              </div>

              {/* Preview rapide */}
              {(clientName || repairNum || pickupDate) && (
                <div className="bg-gray-900 border border-gray-600 rounded-lg p-2 font-mono text-[10px] text-gray-400 space-y-0.5">
                  <p className="text-gray-500">── Aperçu ticket ──</p>
                  {clientName && <p>Client : <span className="text-white font-bold">{clientName}</span></p>}
                  {repairNum  && <p>N° Réparation : <span className="text-white">{repairNum}</span></p>}
                  {pickupDate && <p>Récupération : <span className="text-white">{new Date(pickupDate).toLocaleString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span></p>}
                </div>
              )}

              {/* Reset */}
              {(clientName || repairNum || pickupDate) && (
                <button onClick={() => { setClientName(''); setRepairNum(''); setPickupDate(''); }}
                  className="w-full text-[10px] text-gray-500 hover:text-red-400 py-1 transition-colors">
                  Effacer les infos client
                </button>
              )}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-2 gap-2">
          <button onClick={saveToHistory} disabled={!achat || saving} data-testid="save-history-btn"
            className="flex items-center justify-center gap-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white font-bold py-3 rounded-2xl text-xs transition-all active:scale-95 border border-gray-600">
            <History size={13} /> Historique
          </button>
          <button onClick={copyForClient} data-testid="copy-client-btn"
            className="flex items-center justify-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 rounded-2xl text-xs transition-all active:scale-95">
            {copied ? <><Check size={13} /> Copié !</> : <><Copy size={13} /> Copier client</>}
          </button>
        </div>

        {/* Product action buttons */}
        {isEditMode ? (
          <div className="space-y-2">
            <button onClick={updateProduct} disabled={!achat || saving} data-testid="update-product-btn"
              className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-white font-black py-3.5 rounded-2xl text-sm transition-all active:scale-95">
              {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Edit3 size={15} />}
              Modifier cette fiche
            </button>
            <button onClick={duplicateAsNewSheet} disabled={saving} data-testid="duplicate-product-btn"
              className="w-full flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white font-bold py-3 rounded-2xl text-sm transition-all active:scale-95 border border-gray-600">
              <Plus size={14} /> Créer fiche à ajouter
            </button>
          </div>
        ) : (
          <button onClick={createProduct} disabled={!achat || saving} data-testid="save-product-btn"
            className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-white font-black py-3.5 rounded-2xl text-sm transition-all active:scale-95">
            {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <FileText size={15} />}
            Valider fiche produit
          </button>
        )}

        <button onClick={handlePrint} data-testid="print-pos-btn"
          className="w-full flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-200 font-semibold py-2.5 rounded-2xl text-xs transition-all active:scale-95">
          <Printer size={13} /> Imprimer ticket POS
        </button>

        {/* ── Étiquette vente produit ── */}
        <button onClick={handlePrintSaleLabel} data-testid="toggle-sale-panel-btn"
          className="w-full flex items-center justify-center gap-2 bg-amber-900/30 hover:bg-amber-800/40 border border-amber-700/50 text-amber-300 hover:text-amber-200 font-semibold py-2.5 rounded-2xl text-xs transition-all active:scale-95">
          <Tag size={13} /> Étiquette vente produit
        </button>

        <button onClick={() => setShowPrintConfig(true)} data-testid="print-config-btn"
          className="w-full flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-500 hover:text-gray-300 py-2 rounded-2xl text-xs transition-all">
          <Settings size={11} /> Configurer impression (QZ Tray)
        </button>
      </div>
    </div>

    {/* ── Print Config Modal ── */}
    {showPrintConfig && (
      <PrintConfigModal
        onClose={() => setShowPrintConfig(false)}
        onConfigSaved={() => setShowPrintConfig(false)}
      />
    )}

    {/* ── Label Preview Modal ── */}
    {labelPreview && (
      <LabelPreviewModal
        type={labelPreview.type}
        initial={labelPreview.initial}
        onClose={() => setLabelPreview(null)}
      />
    )}
    </>
  );
};

// ── HISTORY SECTION ───────────────────────────────────────────────────────────
const HistorySection = ({ refresh }) => {
  const [history, setHistory] = useState([]);
  const [search, setSearch]   = useState('');
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy]   = useState('date');
  const [collapsed, setCollapsed] = useState(true);
  const debounce = useRef(null);

  const fetchHistory = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/pricing/history?limit=100${q ? `&search=${encodeURIComponent(q)}` : ''}`, { headers: authHdr() });
      if (r.ok) setHistory(await r.json());
    } catch { } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchHistory(search); }, [fetchHistory, refresh]);

  const handleSearch = (val) => {
    setSearch(val); clearTimeout(debounce.current);
    debounce.current = setTimeout(() => fetchHistory(val), 400);
  };

  const del = async (id) => {
    await fetch(`${API_URL}/api/pricing/history/${id}`, { method: 'DELETE', headers: authHdr() });
    setHistory(p => p.filter(h => h.id !== id)); toast.success('Supprimé');
  };

  const sorted = [...history].sort((a, b) => {
    if (sortBy === 'marge') return b.taux_marge - a.taux_marge;
    if (sortBy === 'prix')  return b.prix_vente_ttc - a.prix_vente_ttc;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const avg = history.length ? history.reduce((s, h) => s + h.taux_marge, 0) / history.length : 0;
  const total = history.reduce((s, h) => s + h.marge_brute, 0);

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-2xl overflow-hidden shadow-lg">
      <button onClick={() => setCollapsed(s => !s)} className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-700 hover:bg-gray-800 transition-colors">
        <div className="flex items-center gap-2">
          <History size={14} className="text-gray-400" />
          <span className="text-white font-semibold text-sm">Historique des calculs</span>
          {history.length > 0 && <span className="bg-gray-700 text-gray-300 text-xs px-2 py-0.5 rounded-full border border-gray-600">{history.length}</span>}
        </div>
        <div className="flex items-center gap-3">
          {history.length > 0 && <span className="text-xs text-gray-500">Marge moy. <span className={avgMarge(avg)}>{fmtP(avg)}</span> · <span className="text-green-400">{fmt(total)}</span></span>}
          {collapsed ? <ChevronDown size={13} className="text-gray-500" /> : <ChevronUp size={13} className="text-gray-500" />}
        </div>
      </button>
      {!collapsed && (
        <div className="p-3 space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input value={search} onChange={e => handleSearch(e.target.value)} placeholder="Rechercher..." data-testid="history-search"
                className="w-full bg-gray-800 border border-gray-600 text-white text-xs rounded-xl pl-8 pr-3 py-2 focus:outline-none focus:border-amber-400 placeholder-gray-600" />
            </div>
            {['date', 'marge', 'prix'].map(s => (
              <button key={s} onClick={() => setSortBy(s)}
                className={`text-xs px-2.5 py-2 rounded-xl border ${sortBy === s ? 'bg-amber-500 text-white border-amber-400 font-bold' : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600'}`}>
                {s === 'date' ? 'Date' : s === 'marge' ? 'Marge' : 'Prix'}
              </button>
            ))}
          </div>
          {loading ? <div className="text-center text-gray-500 text-sm py-4">Chargement...</div>
            : sorted.length === 0 ? <div className="text-center text-gray-500 text-sm py-4">{search ? `Aucun résultat pour "${search}"` : 'Aucun calcul'}</div>
            : (
              <div className="space-y-1.5 max-h-56 overflow-y-auto">
                {sorted.map(h => {
                  const name = [h.brand_label, h.model_label, h.part_label].filter(Boolean).join(' · ') || h.device || h.quality;
                  return (
                    <div key={h.id} className="group flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 hover:border-gray-500 transition-all">
                      <span className="text-sm shrink-0">{QUALITY_MAP[h.quality]?.icon || '?'}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-white text-xs font-semibold truncate">{name}</span>
                          <span className={`text-xs font-bold shrink-0 ${margeColor(h.taux_marge)}`}>{fmtP(h.taux_marge)}</span>
                        </div>
                        <span className="text-gray-500 text-xs">{fmt(h.prix_achat_ht)} → <span className="text-amber-400 font-bold">{fmt(h.prix_vente_ttc)}</span> · {new Date(h.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</span>
                      </div>
                      <button onClick={() => del(h.id)} className="opacity-0 group-hover:opacity-100 p-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-all shrink-0"><Trash2 size={11} /></button>
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      )}
    </div>
  );
};

function avgMarge(avg) { return avg >= 50 ? 'text-green-400' : 'text-yellow-400'; }

// ── SETTINGS SECTION ──────────────────────────────────────────────────────────
const SettingsSection = ({ config, onUpdated }) => {
  const [qc, setQc]     = useState({});
  const [cm, setCm]     = useState({});
  const [tva, setTva]   = useState(0.20);
  const [qt, setQt]     = useState([]);  // quality_types array
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState(false);
  const [tab, setTab]   = useState('coefficients'); // 'coefficients' | 'qualities'

  // New quality form
  const [newQKey, setNewQKey]   = useState('');
  const [newQLabel, setNewQLabel] = useState('');
  const [newQIcon, setNewQIcon]   = useState('⭐');
  const [newQWarranty, setNewQWarranty] = useState('6 mois');

  useEffect(() => {
    if (config) {
      setQc(config.quality_coefficients || {});
      setCm(config.complexity_multipliers || {});
      setTva(config.tva_rate ?? 0.20);
      setQt(config.quality_types || []);
    }
  }, [config]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/pricing/config`, { method: 'PUT', headers: authHdr(), body: JSON.stringify({ quality_coefficients: qc, complexity_multipliers: cm, tva_rate: tva, quality_types: qt }) });
      if (!r.ok) throw new Error((await r.json()).detail);
      toast.success('Configuration sauvegardée !'); onUpdated?.();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const addQuality = () => {
    const key = newQKey.trim().replace(/\s+/g, '_').toLowerCase();
    if (!key || !newQLabel.trim()) { toast.error('Clé et libellé requis'); return; }
    if (qt.find(q => q.key === key)) { toast.error('Cette qualité existe déjà'); return; }
    setQt(prev => [...prev, { key, label: newQLabel.trim(), icon: newQIcon, warranty: newQWarranty, color: 'text-yellow-300' }]);
    setQc(prev => ({ ...prev, [key]: 2.0 }));
    setNewQKey(''); setNewQLabel(''); setNewQIcon('⭐'); setNewQWarranty('6 mois');
    toast.info('Qualité ajoutée — cliquez Sauvegarder pour confirmer');
  };

  const removeQuality = (key) => {
    setQt(prev => prev.filter(q => q.key !== key));
    setQc(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const Stepper = ({ label, value, onChange }) => (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <span className="text-gray-300 text-sm">{label}</span>
      <div className="flex items-center gap-2">
        <button onClick={() => onChange(Math.max(1.0, +(value - 0.05).toFixed(2)))} className="w-8 h-8 flex items-center justify-center bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-200 text-lg font-bold border border-gray-600">−</button>
        <span className="text-white font-mono text-base w-12 text-center">{value.toFixed(2)}</span>
        <button onClick={() => onChange(Math.min(6.0, +(value + 0.05).toFixed(2)))} className="w-8 h-8 flex items-center justify-center bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-200 text-lg font-bold border border-gray-600">+</button>
      </div>
    </div>
  );

  const CM_LABELS = { simple: 'Simple', moyen: 'Moyen', complexe: 'Complexe' };

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-2xl overflow-hidden shadow-lg">
      <button onClick={() => setShow(s => !s)} className="w-full flex items-center justify-between px-4 py-3.5 border-b border-gray-700 hover:bg-gray-800 transition-colors">
        <div className="flex items-center gap-2"><Settings size={15} className="text-gray-400" /><span className="text-white font-semibold text-sm">Paramètres de marge &amp; Qualités</span></div>
        {show ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
      </button>
      {show && (
        <div className="p-4 space-y-4">
          {/* Tabs */}
          <div className="flex gap-1 bg-gray-800 p-1 rounded-xl">
            {[['coefficients', '📊 Coefficients'], ['qualities', '🏅 Qualités']].map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${tab === id ? 'bg-amber-500 text-white shadow-sm' : 'text-gray-400 hover:text-gray-200'}`}>
                {label}
              </button>
            ))}
          </div>

          {tab === 'coefficients' && (
            <>
              <div>
                <p className="text-gray-500 text-sm font-semibold mb-2">Qualité</p>
                <div className="bg-gray-800 border border-gray-700 rounded-xl px-4 divide-y divide-gray-700">
                  {qt.map(q => (
                    <Stepper key={q.key} label={`${q.icon} ${q.label}`} value={qc[q.key] ?? 2.0} onChange={val => setQc(p => ({ ...p, [q.key]: val }))} />
                  ))}
                  {Object.entries(qc).filter(([k]) => !qt.find(q => q.key === k)).map(([k, v]) => (
                    <Stepper key={k} label={k} value={v} onChange={val => setQc(p => ({ ...p, [k]: val }))} />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-gray-500 text-sm font-semibold mb-2">Complexité</p>
                <div className="bg-gray-800 border border-gray-700 rounded-xl px-4 divide-y divide-gray-700">
                  {Object.entries(cm).map(([k, v]) => <Stepper key={k} label={CM_LABELS[k] || k} value={v} onChange={val => setCm(p => ({ ...p, [k]: val }))} />)}
                </div>
              </div>
              <div>
                <p className="text-gray-500 text-sm font-semibold mb-2">TVA</p>
                <div className="bg-gray-800 border border-gray-700 rounded-xl px-4"><Stepper label="Taux TVA" value={tva} onChange={setTva} /></div>
              </div>
            </>
          )}

          {tab === 'qualities' && (
            <div className="space-y-3">
              <p className="text-gray-500 text-sm">Gérez vos types de qualité — ils apparaissent dans le calculateur et les fiches.</p>
              {/* Existing qualities */}
              <div className="space-y-1.5 max-h-52 overflow-y-auto">
                {qt.map((q, i) => (
                  <div key={q.key} className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5">
                    <span className="text-lg shrink-0">{q.icon}</span>
                    <div className="flex-1 min-w-0">
                      <input value={q.label} onChange={e => setQt(prev => prev.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                        className="bg-transparent text-white text-sm font-semibold w-full focus:outline-none focus:text-amber-400" />
                      <input value={q.warranty} onChange={e => setQt(prev => prev.map((x, j) => j === i ? { ...x, warranty: e.target.value } : x))}
                        className="bg-transparent text-gray-500 text-xs w-full focus:outline-none" placeholder="Garantie..." />
                    </div>
                    <input value={q.icon} onChange={e => setQt(prev => prev.map((x, j) => j === i ? { ...x, icon: e.target.value } : x))}
                      className="bg-gray-700 text-center text-lg w-10 rounded-lg focus:outline-none border border-gray-600" />
                    <button onClick={() => removeQuality(q.key)} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"><Trash2 size={12} /></button>
                  </div>
                ))}
              </div>
              {/* Add new quality */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 space-y-2">
                <p className="text-amber-400 text-xs font-bold">Ajouter une qualité</p>
                <div className="grid grid-cols-2 gap-2">
                  <input value={newQKey} onChange={e => setNewQKey(e.target.value)} placeholder="Clé (ex: ultra_oled)"
                    className="bg-gray-800 border border-gray-600 text-white text-xs rounded-lg px-2 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500 placeholder-gray-500" />
                  <input value={newQLabel} onChange={e => setNewQLabel(e.target.value)} placeholder="Libellé (ex: Ultra OLED)"
                    className="bg-gray-800 border border-gray-600 text-white text-xs rounded-lg px-2 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500 placeholder-gray-500" />
                  <input value={newQIcon} onChange={e => setNewQIcon(e.target.value)} placeholder="Icône" maxLength={2}
                    className="bg-gray-800 border border-gray-600 text-white text-sm text-center rounded-lg px-2 py-2 focus:outline-none placeholder-gray-500" />
                  <input value={newQWarranty} onChange={e => setNewQWarranty(e.target.value)} placeholder="Garantie"
                    className="bg-gray-800 border border-gray-600 text-white text-xs rounded-lg px-2 py-2 focus:outline-none placeholder-gray-500" />
                </div>
                <button onClick={addQuality} className="w-full bg-amber-500 hover:bg-amber-400 text-white font-bold py-2 rounded-xl text-sm transition-all flex items-center justify-center gap-2">
                  <Plus size={14} /> Ajouter la qualité
                </button>
              </div>
            </div>
          )}

          <button onClick={saveConfig} disabled={saving} data-testid="save-config-btn"
            className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-white font-black py-4 rounded-2xl flex items-center justify-center gap-2 text-sm transition-all">
            {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Save size={14} />}
            Sauvegarder la configuration
          </button>
        </div>
      )}
    </div>
  );
};

// ── MAIN ──────────────────────────────────────────────────────────────────────
export const PricingTool = () => {
  const [config, setConfig]         = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts]     = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [liveInputs, setLiveInputs] = useState({ achat: '', quality: 'premium', complexity: 'simple', concurrence: '', customPrice: '' });
  const [showSupplier, setShowSupplier] = useState(false);
  const [showCatalog, setShowCatalog]   = useState(false);
  const [importedPrice, setImportedPrice] = useState(null);
  const [catalogFilter, setCatalogFilter] = useState({ initialTab: 'all', initialBrand: '', initialSearch: '' });

  const result = computeLive(liveInputs.achat, liveInputs.quality, liveInputs.complexity, liveInputs.concurrence, config, liveInputs.customPrice);

  const fetchConfig     = useCallback(async () => {
    try { const r = await fetch(`${API_URL}/api/pricing/config`, { headers: authHdr() }); if (r.ok) setConfig(await r.json()); } catch { }
  }, []);

  const fetchCategories = useCallback(async () => {
    try { const r = await fetch(`${API_URL}/api/pricing/categories`, { headers: authHdr() }); if (r.ok) setCategories(await r.json()); } catch { }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/pricing/products`, { headers: authHdr() });
      if (r.ok) setProducts(await r.json());
    } catch { }
  }, []);

  useEffect(() => { fetchConfig(); fetchCategories(); }, [fetchConfig, fetchCategories]);
  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleProductSaved = (type, data) => {
    if (type === 'history')  setHistoryRefresh(r => r + 1);
    if (type === 'product')  { fetchProducts(); setSelectedProduct(null); }
    if (type === 'select')   setSelectedProduct(data);
    if (type === 'refresh_cats') fetchCategories();
  };

  // Ouvre le catalogue pré-filtré depuis le panneau gauche
  const openCatalogWithFilter = (filter) => {
    setCatalogFilter({ initialTab: 'all', initialBrand: '', initialSearch: '', ...filter });
    setShowCatalog(true);
  };

  return (
    <div className="space-y-4 w-full">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap bg-gray-900 border border-gray-700 rounded-2xl px-4 py-3 shadow-sm">
        <div className="flex items-center gap-2">
          <Calculator size={20} className="text-amber-500" />
          <div>
            <h2 className="text-white font-black text-xl">Tarification & Marge</h2>
            <p className="text-gray-400 text-xs">Calculez · Créez des fiches · Mémorisez</p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => { setCatalogFilter({ initialTab: 'all', initialBrand: '', initialSearch: '' }); setShowCatalog(true); }} data-testid="open-catalog-btn"
            className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 border border-amber-400 text-white px-3 py-2 rounded-xl transition-all text-xs font-bold">
            <BookOpen size={13} /> Catalogue
          </button>
          <button onClick={() => setShowSupplier(true)} data-testid="open-supplier-panel-btn"
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 border border-blue-500 text-white px-3 py-2 rounded-xl transition-all text-xs font-bold">
            <ShoppingCart size={13} /> Fournisseur
          </button>
          {products.length > 0 && (
            <div className="flex items-center gap-2 bg-gray-800 border border-gray-600 rounded-xl px-3 py-2">
              <Tag size={13} className="text-amber-500" />
              <span className="text-gray-200 text-xs font-semibold">{products.length} fiche{products.length > 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showSupplier && (
        <SupplierPanel onClose={() => setShowSupplier(false)}
          onImportPrice={(price) => { setImportedPrice(price); setShowSupplier(false); }} />
      )}
      {showCatalog && (
        <CatalogModal
          onLoadProduct={(p) => { handleProductSaved('select', p); setShowCatalog(false); }}
          onClose={() => { setShowCatalog(false); setCatalogFilter({ initialTab: 'all', initialBrand: '', initialSearch: '' }); }}
          onNewProduct={() => setShowCatalog(false)}
          qualityTypes={config?.quality_types || []}
          initialTab={catalogFilter.initialTab}
          initialBrand={catalogFilter.initialBrand}
          initialSearch={catalogFilter.initialSearch}
        />
      )}

      {/* 2-column layout: Left panel (wider) + Center */}
      <div className="flex gap-4 items-start" style={{ minHeight: '600px' }}>
        <LeftPanel
          categories={categories}
          onCatsRefresh={fetchCategories}
          onOpenCatalog={openCatalogWithFilter}
        />

        {/* Center column — calc + history + settings */}
        <div className="flex-1 min-w-0 space-y-4">
          <PricingCalcPanel
            config={config}
            categories={categories}
            selectedProduct={selectedProduct}
            onProductSaved={handleProductSaved}
            onCatsRefresh={fetchCategories}
            onClearProduct={() => setSelectedProduct(null)}
            liveInputs={liveInputs}
            setLiveInputs={setLiveInputs}
            importedPrice={importedPrice}
            onImportedPriceConsumed={() => setImportedPrice(null)}
            qualityTypes={config?.quality_types || []}
            onConfigUpdated={fetchConfig}
          />
          <HistorySection refresh={historyRefresh} />
          <SettingsSection config={config} onUpdated={fetchConfig} />
        </div>
      </div>
    </div>
  );
};
